<template lang="pug">
  .cart-step
    validation-provider(:rules="{ required: { allowFalse: false } }")
      input.hidden(type="checkbox" :value="!!valid")

    .invalid.mb-8(v-if="!valid && !pagination.loading")
      .alert.warning
        p Einige Artikel scheinen nicht mehr verfügbar zu sein, oder haben sich verändert.
        p Bitte überprüfen Sie Ihre Bestellung.
    .relative
      Spinner(v-if="pagination.loading" :size="10" :thickness="2")
      ul(v-if="pagination.items && pagination.items.length")
        li.grid-labels
          .price-label Einzelpreis
          .qty-label Anzahl
          .sum-label Summe
        li.product(v-for="product in pagination.items")
          CartProductSummary(
            :key="product.id"
            :product="product"
            :cart-article-map="cartArticleMap"
            @refresh-cart="$emit('refresh-cart')"
          )
        //- li
          .price-label Einzelpreis
          .qty-label Anzahl
          .sum-label Summe
      Pagination(
        v-if="pagination.pageInfo && pagination.pageInfo.lastPage > 1"
        :last-page="pagination.pageInfo.lastPage"
        :current-page="pagination.pageInfo.currentPage"
        nav-class="pagination is-small is-rounded is-centered"
        @change="page => $emit('change-page', page)"
      )
        template(v-slot:prev)
          span.icon
            i.fas.fa-arrow-left
        template(v-slot:next)
          span.icon
            i.fas.fa-arrow-right
    .bottom-row
      form.coupon-input(v-if="couponInput" @submit.prevent.stop="applyCoupon")
        .coupon-label.is-large Geschenkkarten & Gutscheincodes
        fieldset.form-field.flex
          input#coupon.input(
            type="text"
            name="coupon"
            v-model="couponForm.code"
            :disabled="couponForm.loading"
            placeholder="Code eingeben"
          )
          button.button.submit(
            type="submit"
            aria-label="submit"
            :disabled="couponForm.loading"
          )
            nobr
              span.button-loading-icon.spin(v-if="couponForm.loading" icon="circle-notch")
              font-awesome-icon(v-else icon="check")
              span.ml-2 Einlösen
        CouponList
      .customer-coupons(v-else)
        .coupon-label.is-large(v-if="checkout.form.coupons.length") Gutscheine & Rabatte
        CouponList
      .summary
        .summary-label.text-right.is-large Zusammenfassung
        template(v-if="summary && summary.length")
          template(v-for="row in summary")
            .summary-label.text-right
              span.add-space-after {{ row.label }}
            .summary-value.text-right
              b
                span(v-if="row.key.startsWith('discount')") -
                span {{ row.amount | moneyFormat(currency) }}
          hr
        template(v-if="taxTotal")
          .summary-label.text-right
            //- (v-if="!(customer && customer.show_net_prices && !customer.tax_free)")
            span.add-space-after Enthaltene MwSt.
          .summary-value.text-right
            b {{ taxTotal | moneyFormat(currency) }}
        .summary-label.text-right Gesamtsumme
        .summary-value.text-right
          b {{ grandTotal | moneyFormat(currency) }}
        //- .next-step-slot
        //-   slot(name="next")
</template>

<script>
import uniqid from 'uniqid';
import { mapGetters, mapActions } from 'vuex';

import { get } from '@/helpers';

import { ValidationProvider } from 'vee-validate';
import Pagination from '@/components/Pagination';
import CartProductSummary from '@/extensions/shop/components/CheckoutStepper/Steps/Partials/CartProductSummary';
import CouponList from '@/extensions/shop/components/CheckoutStepper/Steps/Partials/CouponList';

export default {
  name: 'CartStep',
  props: {
    pagination: {
      type: Object,
      required: true
    },
    sections: {
      type: Array,
      required: false
    }
  },
  data: () => ({
    couponForm: {
      loading: false,
      code: undefined
    }
  }),
  components: {
    Pagination,
    CartProductSummary,
    CouponList,
    ValidationProvider
  },
  computed: {
    couponInput() {
      const sections = this.sections || [];
      const section = sections.find(el => el.key === 'coupon-input');
      return section;
    },
    autoApplyCustomerCoupons() {
      let apply = true; // default value: true

      const settings = get(this.couponInput, 'settings') || [];
      const setting = settings.find(el => el.key === 'auto-apply-customer-discounts');
      if (setting) {
        apply = ['true', '1', 'yes', true].includes(setting.value);
      }

      return apply;
    },
    currency() {
      return (
        get(this.customer, 'customerGroup.currency.iso4217') ||
        get(this.defaultCustomerGroup, 'customerGroup.currency.iso4217') ||
        'EUR'
      );
    },
    net() {
      return this.cart.sum - this.cart.tax;
    },
    valid() {
      // check if every article is available and valid
      let valid = true;
      this.cart.articlePivots.forEach(el => {
        if (valid === false) return;
        if (!el.available || !el.valid) valid = false;
      });
      return valid;
    },
    cartArticleMap() {
      const articlePivots = get(this.cart, 'articlePivots') || [];

      let map = {};
      articlePivots.forEach(el => {
        if (!map[el.article.id]) map[el.article.id] = [];
        map[el.article.id].push(el);
      });

      // const map = articles.map(el => {
      //   let article = {};
      //   article[el.id] = el;
      //   return article;
      // });
      return map;
    },
    ...mapGetters({
      customer: 'shop/customer',
      defaultCustomerGroup: 'shop/defaultCustomerGroup',
      // shippingFee: 'shop/shippingFee',
      taxTotal: 'shop/taxTotal',
      grandTotal: 'shop/grandTotal',
      checkout: 'shop/checkout',
      summary: 'shop/cartSummary',
      cart: 'shop/cart'
    })
  },
  methods: {
    applyCoupon() {
      if (!this.couponForm.code) {
        const errorMessage = {
          id: uniqid(),
          title: 'Ungültiges Formular',
          text: 'Tragen Sie einen Gutscheincode ein',
          type: 'warning',
          autoclose: 4000
        };
        this.addMessage(errorMessage);
      } else {
        this.couponForm.loading = true;
        this.applyCouponRequest(this.couponForm.code)
          .then(() => {
            this.couponForm.code = undefined;
          })
          .finally(() => {
            this.couponForm.loading = false;
          });
      }
    },
    ...mapActions({
      addMessage: 'message/add',
      applyCouponRequest: 'shop/applyCoupon'
    })
  }
};
</script>

<style lang="sass" scoped>
@import "~@/assets/sass/variables"

.cart-step
  ul
    & > li.grid-labels
      display: none
    & > li.product
      @apply mb-6 pb-6
      // @apply px-3 py-2 bg-gray-200 rounded
      border-bottom: 2px solid theme('colors.gray.300')
  .summary
    @apply grid
    grid-template-rows: auto
    grid-template-columns: auto max-content
    grid-gap: 0.8rem
    grid-row-gap: 4px
    margin-top: 1rem
    margin-bottom: 0.6rem
    .summary-label
      grid-column: 1
      &.is-large
        @apply text-xl
        grid-column: 1 / 3

  @screen md
    ul, .summary
      @apply grid
      grid-template-rows: auto
      grid-template-columns: auto repeat(3, max-content)
      grid-gap: .8rem
      grid-row-gap: 4px

    .spinner-container
      // background: rgba(255, 255, 255, .5)
      background-color: rgba(238, 238, 241, 0.5)

      position: absolute
      width: 100%
      height: 100%
      z-index: 1

    ul
      & > li.grid-labels
        display: contents
      & > li.product + li.product::v-deep
        .cart-product-summary > .margin-top
          margin-top: 2rem
      ::v-deep
        .article-pivot
          display: contents
      .price-label
        @apply text-right
        grid-column-start: 2
      .qty-label
        @apply text-center
        grid-column-start: 3
      .sum-label
        @apply text-right
        grid-column-start: 4
      li, .cart-product-summary
        display: contents
      ::v-deep
        .info
          grid-column-start: 1
    .pagination-container
      margin-top: 2rem
    .summary
      grid-template-columns: auto max-content
      grid-template-rows: min-content
      grid-auto-rows: min-content
      margin-top: 2rem
      margin-bottom: 2rem
      hr
        grid-column: 1/3
        margin: 1rem 0
      .summary-label
        grid-column: 1
        &.is-large
          @apply text-xl
          grid-column: 1 / 3

    .bottom-row
      display: flex
      width: 100%
      justify-content: space-between

    .coupon-input,
    .customer-coupons
      width: 400px
      margin-top: 2rem
      margin-bottom: 2rem

    #coupon
      @apply border-r-0 rounded-r-none
    .button.submit
      @apply rounded rounded-l-none

    .is-large
      @apply text-xl


.button-loading-icon
  @apply inline-block ease-linear rounded-full
  width: 1rem
  height: 1rem
  border-width: 2px
  border-color: rgba(255, 255, 255, 0.3)
  border-top-color: rgba(255, 255, 255, 1)
  margin-bottom: -3px

.spin
  animation: spin 1.5s linear infinite

@keyframes spin
  0%
    transform: rotate(0deg)
  100%
    transform: rotate(360deg)
</style>
